import {DataTestAttrs} from '@benchling/js-test-utils';
import React from 'react';

import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import {BenchlingNodeStatus} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeConstants';
import {
  SectionsSummary,
  SectionsSummaryProps,
} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeSummary';
import {
  StandardHeader,
  StandardHeaderProps,
} from 'ui-library/reactflow/nodes/BenchlingNode/standard/StandardHeader';
import {
  StandardSections,
  StandardSectionsProps,
} from 'ui-library/reactflow/nodes/BenchlingNode/standard/StandardSections';

export interface StandardNodeProps {
  /** Whether the node should be disabled */
  isDisabled?: boolean;
  /** Whether the node is selected */
  isSelected?: boolean;
  /** Whether the node can be selected */
  isSelectable?: boolean;
  /** The current status of the node */
  status?: BenchlingNodeStatus;
  /** A description of the node's status. When provided, this will be shown in a tooltip on the status icon */
  statusDescription?: string | JSX.Element;
  /** A {@link StandardHeaderProps}. If not provided, no header will be rendered. */
  headerProps?: StandardHeaderProps;
  /** A {@link StandardSectionsProps}. If not provided, no sections will be rendered. */
  sectionsProps?: StandardSectionsProps;
  /** A {@link SectionsSummaryProps}. If not provided, no sections summary will be rendered. */
  sectionsSummaryProps?: SectionsSummaryProps;
  /** Optional width override for the node. If not provided, uses BenchlingNode's default width. */
  width?: string;
  /** Whether this node represents a preview of behavior that is suppressed in the current context. */
  isPreview?: boolean;
  /** Data test attributes for the node */
  dataTestAttrs?: Omit<DataTestAttrs, 'component'>;
}

/**
 * A standard flowchart node.
 * @package
 */
export function StandardNode(props: StandardNodeProps): JSX.Element {
  const {
    isDisabled,
    isSelected,
    isSelectable = true,
    headerProps,
    sectionsProps,
    sectionsSummaryProps,
    status,
    statusDescription,
    width,
    isPreview,
    dataTestAttrs,
  } = props;
  return (
    <BenchlingNode
      isSelected={isSelected}
      isSelectable={isSelectable}
      isDisabled={isDisabled}
      status={status}
      statusDescription={statusDescription}
      w={width}
      isPreview={isPreview}
      dataTestAttrs={dataTestAttrs}
    >
      {headerProps != null && <StandardHeader {...headerProps} />}
      {sectionsSummaryProps != null && <SectionsSummary {...sectionsSummaryProps} />}
      {sectionsProps != null && <StandardSections {...sectionsProps} />}
    </BenchlingNode>
  );
}
